import React, { useState, useRef, useCallback, useMemo } from "react";
import { Box, Button, Tab, Tabs, Typography, IconButton, CircularProgress } from "@mui/material";
import { motion } from "framer-motion";
import axios from "axios";
import { Delete, PictureAsPdf, Image as ImageIcon, Description, InsertDriveFile } from "@mui/icons-material";
import styles from "./FileUpload.module.css";
import { useNavigate } from "react-router-dom";
import uploadIcon from "../../asset/upload.png";

const MAX_FILES = 5;
const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

const FileUpload = ({ onCancel }) => {
  const navigate = useNavigate(); 
  const [tabValue, setTabValue] = useState(0);
  const [fileNames, setFileNames] = useState([]);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (event, newValue) => setTabValue(newValue);

  const validateFiles = (files) => {
    const totalFiles = fileNames.length + files.length;
    if (totalFiles > MAX_FILES) {
      setErrorMessage(`You can only upload up to ${MAX_FILES} files.`);
      return false;
    }
    for (let file of files) {
      if (file.size > MAX_FILE_SIZE) {
        setErrorMessage(`File ${file.name} exceeds ${MAX_FILE_SIZE_MB}MB.`);
        return false;
      }
    }
    return true;
  };

  const handleFilesSelection = useCallback(
    (files) => {
      if (!validateFiles(files)) return;
      const selectedFileNames = Array.from(files).map((file) => file.name);
      setFileNames((prev) => [...prev, ...selectedFileNames]);
      setFilesToUpload((prev) => [...prev, ...files]);
      setErrorMessage("");
    },
    [fileNames]
  );

  const handleFileChange = (event) => handleFilesSelection(event.target.files);
  const handleFileDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    handleFilesSelection(event.dataTransfer.files);
  };
  const handleBrowseClick = () => fileInputRef.current.click();

  const handleCancel = () => {
    setFileNames([]);
    setFilesToUpload([]);
    fileInputRef.current.value = null;
    setErrorMessage("");
    onCancel();
  };

  const handleDeleteFile = (fileNameToDelete) => {
    setFileNames((prev) => prev.filter((name) => name !== fileNameToDelete));
    setFilesToUpload((prev) => prev.filter((file) => file.name !== fileNameToDelete));
  };

  const handleUpload = async () => {
    if (fileNames.length === 0) {
      setErrorMessage("Please select at least one file.");
      return;
    }
    setLoading(true);
    setErrorMessage("");

    const formData = new FormData();
    filesToUpload.forEach((file) => formData.append("files", file));

    try {
      const response = await axios.post(process.env.REACT_APP_SERVER_API, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert(response.data.message);
      handleCancel();
      navigate("/mebot-chat");
    } catch (error) {
      if (error.response) {
        setErrorMessage(error.response.data.message || "Error during upload.");
      } else if (error.request) {
        setErrorMessage("No response from server.");
      } else {
        setErrorMessage("Error: " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderedFileList = useMemo(
    () => fileNames.map((name, index) => (
      <motion.div
        key={`${name}-${index}`}
        className={styles.fileItemContainer}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: index * 0.1 }}
      >
        <Box className={styles.fileItemBox}>
          <Typography className={styles.fileItem}>{name}</Typography>
          <IconButton onClick={() => handleDeleteFile(name)} className={styles.deleteButton}>
            <Delete />
          </IconButton>
        </Box>
      </motion.div>
    )),
    [fileNames]
  );

  return (
    <div className={styles.innerContainer}>
      <Typography variant="h5" className={styles.heading}>CREATE MEBOT</Typography>
      <hr className={styles.line} />

      {/* <Box className={styles.tabsContainer}>
        <Tabs value={tabValue} onChange={handleChange} className={styles.tabs} variant="fullWidth" centered>
          <Tab label="Media" />
        </Tabs>
      </Box> */}

      <Box
        className={`${styles.dragDropBox} ${dragging ? styles.dragging : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleFileDrop}
      >
        {tabValue === 0 && (
          <>
            <img src={uploadIcon} alt="Upload Icon" className={styles.uploadIcon} />
            <Typography className={styles.uploadText}>
              Drag and drop your files here
            </Typography>
            <hr className={styles.dragDropLine} />
            <input type="file" accept=".pdf" multiple ref={fileInputRef} onChange={handleFileChange} style={{ display: "none" }} />
            <Button variant="contained" className={styles.browseButton} onClick={handleBrowseClick}>
              Browse Files
            </Button>
            <Box className={styles.fileTypes}>
              <PictureAsPdf className={styles.fileTypeIcon} />
              <Description className={styles.fileTypeIcon} />
              <InsertDriveFile className={styles.fileTypeIcon} />
              <ImageIcon className={styles.fileTypeIcon} />
            </Box>
            {errorMessage && <Typography className={styles.errorMessage}>{errorMessage}</Typography>}
            {loading && <CircularProgress className={styles.loadingIndicator} />}
            <Typography variant="caption" className={styles.fileLimitations}>
              Max: 5 PDFs, 20MB per file
            </Typography>
          </>
        )}
        {tabValue !== 0 && <Typography className={styles.comingSoonText}>This feature is coming soon!</Typography>}
      </Box>

      {fileNames.length > 0 && (
        <>
          <Typography className={styles.fileHeading}>Uploaded Files:</Typography>
          <Box className={styles.fileListContainer}>{renderedFileList}</Box>
        </>
      )}

      <motion.div className={styles.buttonContainer} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <Button variant="contained" onClick={handleUpload} className={styles.me}>
          Next
        </Button>
      </motion.div>
    </div>
  );
};

export default FileUpload;
